import type { ReactNode } from 'react';
import type { MarkerPositionChangeFn } from '../../../components/Maps/Maps';
import type { UseAppForm } from '../../form';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import Maps from '../../../components/Maps/Maps';
import RequiredTemplate from '../../../components/RequiredTemplate/RequiredTemplate';

import useYjsField from '../../collaboration/useYjsField';
import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

const coordinateInputs = [
  {
    key: 'lat',
    transKey: 'Latitude',
  },
  {
    key: 'lon',
    transKey: 'Longitude',
  },
];

type GeoData = {
  lat?: number;
  lon?: number;
};

interface GeoFieldProps {
  /**
   * Field label above maps
   */
  label: ReactNode;
  /**
   * If field is required
   */
  required: boolean;
  /**
   * If field is disabled
   */
  disabled: boolean;
  /**
   * If dropdown is readonly (non-interactive but without disabled classes)
   */
  readonly: boolean;
  /**
   * On focus callback
   */
  onFocus: () => void;
  /**
   * Help text under lat lon inputs
   */
  helpText: ReactNode;
  /**
   * Additional CSS classes for geo input
   */
  additionalClasses: string;
  /**
   * Test id for fields
   */
  testId: string;
}

const GeoField = ({
  label = '',
  required = false,
  disabled = false,
  readonly = false,
  onFocus,
  helpText = '',
  additionalClasses = '',
  testId = '',
}: GeoFieldProps) => {
  const { t } = useTranslation();
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext<GeoData>();
  const errors = useFieldErrors(field.name);

  const yjsField = useYjsField('raw');

  const isLatLonEdited = yjsField.isBeingEdited;

  const markerPosition = useMemo(
    () => ({
      lat: field.state.value?.lat || 0,
      lng: field.state.value?.lon || 0,
    }),
    [field.state.value?.lat, field.state.value?.lon],
  );

  const onMarkerPositionChange: MarkerPositionChangeFn = (position) => {
    field.handleChange(position);
    field.handleBlur();

    const yjsInstance = yjsField.getYjsInstance();
    if (yjsInstance) {
      yjsInstance.set('lat', position.lat);
      yjsInstance.set('lon', position.lon);
    }
  };

  return (
    <div
      className={twMerge(
        'w-full',
        'flex',
        'flex-col',
        'relative',
        additionalClasses,
      )}
    >
      {label && (
        <label className="text-sm text-slate-400 dark:text-slate-200 mb-1">
          {label}
          {required && <RequiredTemplate />}
        </label>
      )}
      <Maps
        center={markerPosition}
        markerPosition={markerPosition}
        onMarkerPositionChange={onMarkerPositionChange}
        disabled={disabled || readonly || isLatLonEdited}
      />
      <div
        className={twMerge(
          'grid grid-cols-2 gap-2 lg:gap-6 mt-3',
          additionalClasses,
        )}
      >
        {coordinateInputs.map((coordinate) => (
          <form.CtoCustomField
            key={coordinate.key}
            name={`${field.name}.${coordinate.key}`}
            properties={{
              label: t(`ContentForm.${coordinate.transKey}`),
              inputType: 'number',
            }}
            schema={{
              type: 'number',
            }}
            step="0.001"
            onFocus={onFocus}
            disabled={disabled || isLatLonEdited}
            readonly={readonly}
            required={required}
            testId={testId}
          />
        ))}
      </div>
      <HelpErrorTextsTemplate
        helpText={helpText}
        error={errors}
        testId={testId}
      />
    </div>
  );
};

export default GeoField;
